import React from 'react';
import { _T, C, CSS } from '../../shared/theme';
import { s_PB, s_GC } from '../../shared/styles';
import { t } from '../../shared/i18n';

function WeekSummaryScreen({ lang, weekIntention, weekAgenda, userName, onDone }) {
  const nl = lang === 'nl';
  const days = ["mon","tue","wed","thu","fri","sat","sun"].map(d=>t(`day.${d}`,lang));

  const allItems = Object.values(weekAgenda).flat();
  const exerciseCount = allItems.filter(a => a.type === 'exercise').length;
  const restCount    = allItems.filter(a => a.type === 'rest').length;
  const totalCount   = allItems.length;

  // Today's day index (0=Mon … 6=Sun), JS getDay() is 0=Sun so shift
  const jsDay = new Date().getDay();
  const todayIdx = jsDay === 0 ? 6 : jsDay - 1;
  const todayItems = weekAgenda[todayIdx] || [];

  return (
    <div style={{
      minHeight: '100vh',
      background: `linear-gradient(160deg,${_T.bg},${_T.bg2})`,
      display: 'flex',
      flexDirection: 'column',
      padding: '24px 24px 32px',
      paddingTop: 52,
    }}>
      <style>{CSS}</style>

      {/* Header */}
      <div style={{ fontSize: 9, color: _T.accent, letterSpacing: 3, fontWeight: 600, marginBottom: 6 }}>
        {t('summary.your_week', lang)}
      </div>
      <div style={{ fontFamily: 'Georgia,serif', fontSize: 22, color: C.white, marginBottom: 4, lineHeight: 1.3 }}>
        {userName ? `${t('summary.ready', lang)} ${userName}! 🎉` : t('summary.week_set', lang)}
      </div>
      <div style={{ color: C.silver, fontSize: 13, marginBottom: 20, lineHeight: 1.6 }}>
        {t('summary.overview', lang)}
      </div>

      {/* Intention pill */}
      {weekIntention && (
        <div style={{
          ...s_GC({ padding: '12px 16px', marginBottom: 16 }),
          borderLeft: `3px solid ${_T.accent}`,
        }}>
          <div style={{ fontSize: 10, color: _T.accent, letterSpacing: 2, fontWeight: 600, marginBottom: 4 }}>
            {t('summary.intention_label', lang)}
          </div>
          <div style={{ color: C.white, fontSize: 14, fontStyle: 'italic', lineHeight: 1.5 }}>
            "{weekIntention}"
          </div>
        </div>
      )}

      {/* Stats row */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {[
          { icon: '📅', value: totalCount,   label: t('summary.activities', lang), color: C.silver },
          { icon: '🏃', value: exerciseCount, label: t('dash.workouts', lang),      color: _T.accent },
          { icon: '🌿', value: restCount,     label: t('dash.rest_moments', lang),  color: C.teal },
        ].map(s => (
          <div key={s.label} style={{
            flex: 1,
            ...s_GC({ padding: '12px 8px' }),
            textAlign: 'center',
          }}>
            <div style={{ fontSize: 18, marginBottom: 4 }}>{s.icon}</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: s.color }}>{s.value}</div>
            <div style={{ fontSize: 10, color: C.silver, marginTop: 2 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Day-by-day grid */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 11, color: C.silver, marginBottom: 8, fontWeight: 600 }}>
          {t('summary.daily_plan', lang)}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {days.map((day, i) => {
            const items = weekAgenda[i] || [];
            const isToday = i === todayIdx;
            const exItems = items.filter(a => a.type === 'exercise');
            const restItems = items.filter(a => a.type === 'rest');
            const otherItems = items.filter(a => a.type === 'activity');

            return (
              <div key={i} style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '8px 12px',
                borderRadius: 12,
                background: isToday ? `${_T.accent}12` : _T.bg3,
                borderTop: `1.5px solid ${isToday ? _T.accent : _T.cardBorderTop}`,
                borderBottom: `1px solid ${_T.cardBorderBot}`,
                borderLeft: `1px solid ${isToday ? _T.accent + '44' : 'rgba(255,255,255,0.03)'}`,
                borderRight: '1px solid rgba(255,255,255,0.03)',
              }}>
                {/* Day label */}
                <div style={{ width: 28, flexShrink: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: isToday ? 700 : 400, color: isToday ? _T.accent : C.silver }}>
                    {day}
                  </div>
                  {isToday && (
                    <div style={{ fontSize: 8, color: _T.accent, fontWeight: 600, letterSpacing: 1 }}>
                      {t('summary.today_abbrev', lang)}
                    </div>
                  )}
                </div>

                {/* Activity pills */}
                <div style={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {items.length === 0 ? (
                    <span style={{ fontSize: 11, color: C.silver, fontStyle: 'italic' }}>
                      {t('summary.free', lang)}
                    </span>
                  ) : (
                    <>
                      {exItems.map((a, j) => (
                        <span key={j} style={{
                          fontSize: 11, background: `${_T.accent}20`,
                          border: `1px solid ${_T.accent}55`, borderRadius: 8,
                          padding: '2px 8px', color: _T.accent,
                        }}>{a.icon} {nl ? a.labelNL : a.label}</span>
                      ))}
                      {restItems.map((a, j) => (
                        <span key={j} style={{
                          fontSize: 11, background: `${C.teal}18`,
                          border: `1px solid ${C.teal}55`, borderRadius: 8,
                          padding: '2px 8px', color: C.teal,
                        }}>{a.icon} {nl ? a.labelNL : a.label}</span>
                      ))}
                      {otherItems.map((a, j) => (
                        <span key={j} style={{
                          fontSize: 11, background: `${C.silver}15`,
                          border: `1px solid ${C.silver}33`, borderRadius: 8,
                          padding: '2px 8px', color: C.silver2,
                        }}>{a.icon} {nl ? a.labelNL : a.label}</span>
                      ))}
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Today's focus callout */}
      {todayItems.length > 0 && (
        <div style={{
          ...s_GC({ padding: '14px 16px', marginBottom: 16 }),
          borderLeft: `3px solid ${C.teal}`,
        }}>
          <div style={{ fontSize: 10, color: C.teal, letterSpacing: 2, fontWeight: 600, marginBottom: 6 }}>
            {t('summary.today_plan', lang)}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {todayItems.map((a, i) => (
              <span key={i} style={{ fontSize: 13 }}>{a.icon} {nl ? a.labelNL : a.label}</span>
            ))}
          </div>
        </div>
      )}

      {/* CTA */}
      <div style={{ marginTop: 'auto', paddingTop: 8 }}>
        <button style={s_PB({ width: '100%', fontSize: 16, padding: '16px' })} onClick={onDone}>
          {t('summary.begin_day', lang)}
        </button>
        <div style={{display:"flex",justifyContent:"center",gap:20,paddingTop:16}}>
          <a href="/privacy" style={{color:"#3a4a62",fontSize:11,textDecoration:"none"}}>Privacy Policy</a>
          <a href="/terms" style={{color:"#3a4a62",fontSize:11,textDecoration:"none"}}>Terms &amp; Conditions</a>
        </div>
      </div>
    </div>
  );
}

export default WeekSummaryScreen;
